import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { useFeatureFlag } from '@fs/zion-flags'
import { timestampToDateString } from '../../../../util/DateUtil'
import useCompactQueueEventListeners from './useCompactQueueEventListeners'
import {
  AncestryPersonLink,
  CmisLink,
  MrnLink,
  PersonLink,
  RelationshipLink,
  TreeLink,
  TreePersonLink,
  UserLink,
  treeAncestryIconColor,
} from '../../../app/Links'
import DmcCopyToClipboardTooltip from '../../../../util/DmcCopyToClipboardTooltip'
import { printValueOrBooleanReadable } from '../../../../util/StringUtil'
import {
  LEGACY_SELECTION_BEHAVIOR,
  countShiftRangeDivergence,
  recordLegacySelectionUsage,
} from '../../../../util/LegacySelectionUsage'
import { getQueueFamily } from '../../../../services/QueuesService'
import { queueNames } from '../useMessagesListController'

const personIdCellCss = css`
  display: flex;
  align-items: center;
  gap: 5px;
`

function PersonIdCell({ personId, newTab, memories, showAncestryLink }) {
  const ancestryLinkFlag = useFeatureFlag('data_dmconsole_ancestryLink')
  const [family, setFamily] = useState(null)
  const showAncestry = showAncestryLink && ancestryLinkFlag.isOn

  useEffect(() => {
    if (showAncestry && personId) {
      getQueueFamily(personId)
        .then((data) => setFamily(data))
        .catch(() => {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAncestry, personId])

  return (
    <span css={personIdCellCss}>
      <DmcCopyToClipboardTooltip
        value={personId}
        body={<PersonLink personId={personId} newTab={newTab} />}
        nextToText
      />
      <DmcCopyToClipboardTooltip
        value={personId}
        body={
          <TreePersonLink
            personId={personId}
            treeIcon
            newTab={newTab}
            memories={memories}
            style={showAncestry ? { color: treeAncestryIconColor } : undefined}
          />
        }
        nextToText
      />
      {showAncestry && family && <AncestryPersonLink family={family} />}
    </span>
  )
}

export default function useCompactMessagesListController({ queueState, queueActions, queueCachesState, filterRef }) {
  const [shiftIsPressed, setShiftIsPressed] = useState(false)
  const [activeColumns, setActiveColumns] = useState({ columnList: [] })
  const [contextMenu, setContextMenu] = useState(null)
  const [lastCheckedIndex, setLastCheckedIndex] = useState()
  const zionSelection = useRef({ anchor: null, lastShift: null })

  useEffect(() => {
    zionSelection.current = { anchor: null, lastShift: null }
  }, [queueState.queueContents])

  useCompactQueueEventListeners({
    queueState,
    queueActions,
    contextMenu,
    setContextMenu,
    setShiftIsPressed,
    setLastCheckedIndex,
    filterRef,
  })
  const { t } = useTranslation()

  const queueColumns = useMemo(() => {
    const keys = Array.from(
      new Set(
        queueState?.queueContents?.flatMap((message) =>
          Object.keys(message.details).filter((key) => key !== 'changeId')
        )
      )
    )
    const priority = [
      'name',
      'nameFromMembership',
      'personId',
      'fatherPersonId',
      'motherPersonId',
      'searchPersonId',
      'matchedPersonId',
      'treePersonId',
    ]
    keys.sort((a, b) => {
      const indexA = priority.indexOf(a) === -1 ? 99 : priority.indexOf(a)
      const indexB = priority.indexOf(b) === -1 ? 99 : priority.indexOf(b)
      return indexA - indexB
    })

    return ['created', ...(keys || [])]
  }, [queueState.queueContents])

  const columnMultiSelect = (column) => {
    let start
    let end
    if (activeColumns.lastCheckedIndex < queueColumns.indexOf(column)) {
      start = activeColumns.lastCheckedIndex
      end = queueColumns.indexOf(column)
    } else {
      start = queueColumns.indexOf(column) - 1
      end = activeColumns.lastCheckedIndex - 1
    }
    const updatedCheckboxes = queueColumns.slice(start + 1, end + 1).reduce(
      (acc, col) => {
        return acc.includes(queueColumns[end]) ? acc.filter((c) => c !== col) : [...acc, col]
      },
      [...activeColumns.columnList]
    )

    const excludedFields = queueColumns.filter((col) => !updatedCheckboxes.includes(col))
    localStorage.setItem(`excludedFields_${queueState.queueName}`, JSON.stringify(excludedFields))

    setActiveColumns({
      columnList: updatedCheckboxes,
      lastCheckedIndex: queueColumns.indexOf(column),
    })
  }

  const toggleColumn = (column) => {
    setActiveColumns((prev) => {
      const updatedColumns = prev.columnList.includes(column)
        ? prev.columnList.filter((col) => col !== column)
        : queueColumns.filter((col) => [...prev.columnList, column].includes(col))
      localStorage.setItem(
        `excludedFields_${queueState.queueName}`,
        JSON.stringify(queueColumns.filter((col) => !updatedColumns.includes(col)))
      )
      return { columnList: updatedColumns, lastCheckedIndex: queueColumns.indexOf(column) }
    })
  }

  const handleColumnCheckboxChange = (column) => {
    if (shiftIsPressed && activeColumns.lastCheckedIndex !== undefined) {
      recordLegacySelectionUsage(LEGACY_SELECTION_BEHAVIOR.SHIFT_COLUMN_RANGE, {
        queueName: queueState.queueName,
        rangeSize: Math.abs(queueColumns.indexOf(column) - activeColumns.lastCheckedIndex) + 1,
      })
      columnMultiSelect(column)
    } else {
      setActiveColumns(() => {
        toggleColumn(column)
      })
    }
  }

  const rowMultiSelect = (clickedIndex, event) => {
    // Handle range selection within the filtered list
    const start = Math.min(clickedIndex, lastCheckedIndex)
    const end = Math.max(clickedIndex, lastCheckedIndex)

    for (let i = start; i <= end; i++) {
      queueActions.addCheckboxSelection(queueState.queueContents[i], event.target.checked)
    }
  }

  const handleRowCheckboxChange = (event) => {
    const clickedIndex = parseInt(event.target.name, 10)

    const { anchor, lastShift } = zionSelection.current

    if (shiftIsPressed && lastCheckedIndex !== undefined) {
      const rowsDiffering = countShiftRangeDivergence({
        selected: queueState.queueContents.map((message) => !!queueState.selectedCheckboxes?.[message.id]),
        clickedIndex,
        legacyAnchor: lastCheckedIndex,
        checked: event.target.checked,
        zionAnchor: anchor,
        zionLastShift: lastShift,
      })
      if (rowsDiffering > 0) {
        recordLegacySelectionUsage(LEGACY_SELECTION_BEHAVIOR.SHIFT_ROW_RANGE_DIVERGES, {
          queueName: queueState.queueName,
          rowsDiffering,
          backwards: clickedIndex < lastCheckedIndex,
          chained: lastShift != null,
        })
      }
      rowMultiSelect(clickedIndex, event)
    } else {
      queueActions.addCheckboxSelection(queueState.queueContents[clickedIndex], event.target.checked)
    }

    zionSelection.current =
      shiftIsPressed && anchor != null && anchor !== clickedIndex
        ? { anchor, lastShift: clickedIndex }
        : { anchor: clickedIndex, lastShift: shiftIsPressed ? clickedIndex : null }
    setLastCheckedIndex(clickedIndex)
  }

  const formatField = (message, key = queueState.sortField, memoriesLink = false) => {
    const details = message.details || {}
    let result = {}

    switch (key) {
      case 'created':
        result = {
          value: timestampToDateString({
            timestamp: message.inQueueSince,
            withTime: true,
            timestampIsSeconds: true,
          }),
          sortValue: message.inQueueSince,
        }
        break

      case 'personId':
      case 'fatherPersonId':
      case 'motherPersonId':
      case 'searchPersonId':
      case 'matchedPersonId':
      case 'treePersonId':
      case 'person1Id':
      case 'person2Id':
        result = {
          filterValue: details[key],
          value: (
            <PersonIdCell
              personId={details[key]}
              newTab
              memories={memoriesLink}
              showAncestryLink={queueState.queueName?.includes(queueNames.HOLOCAUST_QUEUES)}
            />
          ),
        }
        break

      case 'coupleId':
      case 'associationId':
      case 'parent_childId': {
        const relationships = {
          coupleId: 'couple',
          associationId: 'association',
          parent_childId: 'parentchild',
        }
        result = {
          filterValue: details[key],
          value: (
            <DmcCopyToClipboardTooltip
              value={details[key]}
              body={<RelationshipLink relationshipId={details[key]} relationshipType={relationships[key]} newTab />}
              nextToText
            />
          ),
        }
        break
      }

      case 'contributorId':
        result = {
          filterValue: details[key],
          value: (
            <DmcCopyToClipboardTooltip
              value={details[key]}
              body={
                <UserLink
                  userName={queueCachesState.usersCache[details[key]]?.contactName}
                  userId={details[key]}
                  newTab
                />
              }
              nextToText
            />
          ),
        }
        break

      case 'mrn':
        result = {
          filterValue: details[key],
          value: (
            <DmcCopyToClipboardTooltip value={details[key]} body={<MrnLink mrn={details[key]} newTab />} nextToText />
          ),
        }
        break

      case 'cmisId':
        result = {
          filterValue: details[key],
          value: (
            <DmcCopyToClipboardTooltip
              value={details[key]}
              body={<CmisLink cmisId={details[key]} newTab />}
              nextToText
            />
          ),
        }
        break

      case 'treeId':
        result = {
          filterValue: details[key],
          value: (
            <DmcCopyToClipboardTooltip value={details[key]} body={<TreeLink treeId={details[key]} />} nextToText />
          ),
        }
        break

      case 'aiScores': {
        const aiScores = details.aiScores
        const aiCategory = aiScores?.categoryScores?.find((cat) => cat.personType === aiScores?.personType)
        if (aiCategory?.value != null) {
          const score = Math.floor((aiCategory.value * 100) / 5) * 5
          if (score)
            result = {
              value: t(`queues.aiCategoryScore.${aiScores?.personType}`, '{type}: {score}', {
                type: aiCategory?.personType,
                score: Math.floor((aiCategory.value * 100) / 5) * 5,
              }),
            }
        }
        break
      }

      case 'duplicatePersonId':
        result = {
          filterValue: details[key],
          value: (
            <DmcCopyToClipboardTooltip
              value={details[key]}
              body={<PersonLink personId={details[key]} newTab forward={false} />}
              nextToText
            />
          ),
        }
        break

      default:
        if (message.details && typeof message.details[key] === 'object') {
          result = { value: JSON.stringify(message.details[key], null, 2) }
        } else {
          result = { value: printValueOrBooleanReadable(details[key]) || '' }
        }
        break
    }
    return result
  }

  const sortIconOnClick = (type, field) => {
    queueState.sortDirection = type
    queueState.sortField = field
    queueActions.setQueueState((prevState) => ({
      ...prevState,
      sortDirection: type,
      sortField: field,
    }))
  }

  const sortByFieldType = (field) => {
    if (field !== queueState.sortField) {
      sortIconOnClick('asc', field)
    } else if (queueState.sortDirection === undefined) {
      sortIconOnClick('asc', field)
    } else if (queueState.sortDirection === 'asc') {
      sortIconOnClick('desc', field)
    } else if (queueState.sortDirection === 'desc') {
      sortIconOnClick(undefined, undefined)
    }
  }

  useEffect(() => {
    const savedExcludedFields = localStorage.getItem(`excludedFields_${queueState.queueName}`)
    const excludedFields = JSON.parse(savedExcludedFields)

    if (queueState.queueContents.length > 0) {
      setActiveColumns({
        columnList: queueColumns.filter((col) => !excludedFields?.includes(col)),
        lastCheckedIndex: 0,
      })
    }
  }, [queueState.queueContents, queueState.queueName, t, queueColumns])

  // Contributor suggestions for Autosuggest

  return {
    compactQueueState: {
      queueColumns,
      shiftIsPressed,
      activeColumns,
      contextMenu,
    },
    compactQueueActions: {
      setShiftIsPressed,
      setActiveColumns,
      handleColumnCheckboxChange,
      handleRowCheckboxChange,
      formatField,
      sortByFieldType,
    },
  }
}
